/**
 * Стани й ролі співробітника — одним переліком для ресурсу, шапки картки,
 * вкладки «Права доступу» й історії змін: підпис і колір мусять збігатися
 * скрізь.
 */
export interface UserOption {
    value: string
    label: string
    color?: string
}

/**
 * Лише два стани (ADR 038): «Запрошений» і «Видалений» в Астеріксі не
 * вживаються — співробітника створюють із паролем, а видалення немає.
 *
 * Кольори — hex, бо так їх розбирає ядровий бейдж колонки й `StatusTag`.
 */
export const USER_STATUS_OPTIONS: UserOption[] = [
    { value: 'active', label: 'Активний', color: '#5FCC00' },
    { value: 'inactive', label: 'Деактивований', color: '#E82626' },
]

/**
 * Ролі, які видають співробітникові (`SPEC-003` §1), — у порядку легасі
 * (`frontend/src/Ship/Configs/DefaultValueConfig.js:4-12`): так стоять
 * перемикачі вкладки «Права доступу».
 *
 * `ROLE_SUPER_MANAGER` — системна заглушка ядра, людям не видається.
 */
export const USER_ROLE_OPTIONS: UserOption[] = [
    { value: 'ROLE_SUPER_ADMIN', label: 'Супер адміністратор' },
    { value: 'ROLE_ADMIN', label: 'Адміністратор' },
    { value: 'ROLE_ACCOUNTANT', label: 'Фінанси' },
    { value: 'ROLE_SENIOR_LOGIST', label: 'Старший логіст' },
    { value: 'ROLE_LOGIST', label: 'Логіст' },
    { value: 'ROLE_MANAGER', label: 'Менеджер' },
    { value: 'ROLE_KAM', label: 'Кам' },
]

/**
 * Базова роль без додаткових прав. Перемикача в неї немає: її має кожен, а
 * підпис потрібен, щоб людина без жодної з ролей вище не світилася в шапці
 * порожнім місцем чи сирим кодом.
 */
export const USER_BASE_ROLE: UserOption = { value: 'ROLE_USER', label: 'Співробітник' }

const ROLE_CODES = new Set(USER_ROLE_OPTIONS.map((option) => option.value))

/** Підпис ролі за кодом; невідомий код лишається як є — його видно й можна з'ясувати. */
export const roleLabel = (code: string): string =>
    USER_ROLE_OPTIONS.find((option) => option.value === code)?.label ??
    (code === USER_BASE_ROLE.value ? USER_BASE_ROLE.label : code)

/** Підпис стану за кодом. */
export const statusLabel = (code: string): string =>
    USER_STATUS_OPTIONS.find((option) => option.value === code)?.label ?? code

/** Видані ролі зі значення поля: лише рядки й лише ті, що мають перемикач. */
export const assignedRoles = (value: unknown): string[] =>
    (Array.isArray(value) ? value : []).filter(
        (role): role is string => typeof role === 'string' && ROLE_CODES.has(role)
    )

/**
 * Новий набір ролей після перемикання — у порядку переліку, без повторів.
 * Порожній набір означає «Співробітник»: бек тримає `ROLE_USER` у кожного.
 */
export const toggleRole = (current: readonly string[], role: string, enabled: boolean): string[] => {
    const next = new Set(assignedRoles(current))

    if (enabled) next.add(role)
    else next.delete(role)

    const ordered = USER_ROLE_OPTIONS.map((option) => option.value).filter((code) => next.has(code))

    return ordered.length > 0 ? ordered : [USER_BASE_ROLE.value]
}
